"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Clock, Mail, MapPin, Phone, Send, ShieldCheck, Sparkles, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const guaranteesList = [{
  id: 1,
  title: "Direct Consultant Access",
  description: "You work directly with me—Yassine Essaadi. No account managers, delegates, or junior staff."
}, {
  id: 2,
  title: "Honest & Realistic Roadmap",
  description: "Clear expectations backed by data. No artificial promises or guaranteed ranking gimmicks."
}, {
  id: 3,
  title: "No Lock-in Contracts",
  description: "Flexible, transparent monthly agreements built on tangible search growth and business value."
}];
const serviceOptions = ["National SEO", "Technical SEO Audit", "Local SEO & Maps", "B2B SEO & Lead Generation", "Ecommerce SEO", "AI Search Optimisation", "International SEO"];
function InnerConsultationForm() {
  const {
    handleSubmit,
    isSubmitted,
    isSubmitting,
    submitError,
    successMessage,
    resetForm,
    register
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="py-12 text-center space-y-4">
        <div className="mx-auto size-16 bg-accent/20 text-accent rounded-full flex items-center justify-center">
          <Send className="size-8" />
        </div>
        <h3 className="text-2xl font-bold font-serif text-foreground">
          Thank You for Reaching Out!
        </h3>
        <p className="text-muted-foreground max-w-md mx-auto text-sm leading-relaxed">
          {successMessage || "I have received your request and will review your website details personally. Expect a response in your inbox within 24 hours."}
        </p>
        <div className="pt-4 flex flex-col sm:flex-row gap-3 justify-center">
          <Button variant="outline" onClick={resetForm} className="rounded-lg">
            Submit Another Request
          </Button>
          <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-lg">
            <Link to="/CaseStudiesPage">
              Explore Case Studies <ArrowRight className="size-4 ml-1.5" />
            </Link>
          </Button>
        </div>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label htmlFor="name" className="text-xs font-semibold text-foreground">
            Your Full Name <span className="text-destructive">*</span>
          </label>
          <input id="name" type="text" required placeholder="Yassine Essaadi" {...register("name", {
          required: true
        })} className="w-full px-3.5 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-shadow" />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="email" className="text-xs font-semibold text-foreground">
            Email Address <span className="text-destructive">*</span>
          </label>
          <input id="email" type="email" required placeholder="you@company.com" {...register("email", {
          required: true
        })} className="w-full px-3.5 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-shadow" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label htmlFor="website" className="text-xs font-semibold text-foreground">
            Website URL <span className="text-destructive">*</span>
          </label>
          <input id="website" type="url" required placeholder="https://yourwebsite.com" {...register("website", {
          required: true
        })} className="w-full px-3.5 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-shadow" />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="service" className="text-xs font-semibold text-foreground">
            Primary Focus Area
          </label>
          <select id="service" {...register("service")} className="w-full px-3.5 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-shadow">
            {serviceOptions.map((opt, i) => <option key={i} data-index={i} value={opt}>
                {opt}
              </option>)}
          </select>
        </div>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="message" className="text-xs font-semibold text-foreground">
          Your Growth Goals or Current SEO Challenges
        </label>
        <textarea id="message" rows={3} placeholder="Tell me a bit about your business, target audience, or specific challenges..." {...register("message")} className="w-full px-3.5 py-2.5 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-shadow resize-none" />
      </div>

      {submitError && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>}

      <div className="pt-2">
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-semibold text-base py-6 rounded-lg cursor-pointer transition-[background-color,transform,box-shadow] hover:scale-[1.01]">
          {isSubmitting ? "Submitting..." : <>
              Book a Free SEO Consultation
              <ArrowRight className="size-5 ml-2" />
            </>}
        </Button>
      </div>

      <div className="flex items-center justify-center gap-2 pt-2 text-xs text-muted-foreground text-center">
        <ShieldCheck className="size-4 text-emerald-600 shrink-0" />
        <span>100% confidential. No spam. Direct response from Yassine within 24h.</span>
      </div>

      <div className="border-t border-border pt-4 mt-4 text-center">
        <p className="text-xs text-muted-foreground">
          Prefer to visit the main contact page?{" "}
          <Link to="/ContactPage" className="text-primary font-semibold hover:underline inline-flex items-center gap-1">
            Go to Contact Page <ArrowRight className="size-3.5 inline" />
          </Link>
        </p>
      </div>
    </form>;
}
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultationcta" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-28 overflow-hidden">
      <div className="absolute top-0 right-0 -z-10 w-96 h-96 bg-secondary/30 rounded-full blur-3xl pointer-events-none transform translate-x-1/3 -translate-y-1/3" />
      <div className="absolute bottom-0 left-0 -z-10 w-96 h-96 bg-muted/50 rounded-full blur-3xl pointer-events-none transform -translate-x-1/3 translate-y-1/3" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          <motion.div variants={itemVariants} className="lg:col-span-6 space-y-8">
            <div>
              <Badge variant="outline" className="mb-4 gap-1.5 py-1 px-3 border-primary/20 text-primary bg-primary/5 rounded-full font-medium">
                <UserCheck className="size-3.5 text-primary" />
                Work Directly With Me
              </Badge>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight leading-tight">
                Ready for Sustainable Search Growth?
              </h2>
              <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                Book a complimentary 30-minute SEO consultation or request a quick website review. I will personally analyze your current technical foundation, keyword visibility, and search potential—with zero pressure or sales scripts.
              </p>
            </div>

            <div className="space-y-4">
              {guaranteesList.map((item, i) => <div key={item.id} data-index={i} className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border shadow-xs">
                  <div className="p-2 rounded-lg bg-accent/10 text-accent shrink-0 mt-0.5">
                    <CheckCircle2 className="size-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-card-foreground">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-0.5 leading-snug">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="p-6 rounded-xl bg-muted/60 border border-border space-y-4">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <Sparkles className="size-4 text-primary" />
                Direct Communication Options
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                <a href="mailto:contact@ye-consultancy.com" className="flex items-center gap-3 p-3 rounded-lg bg-background border border-border text-foreground hover:border-primary/50 transition-[border-color,box-shadow] shadow-2xs group">
                  <div className="p-2 rounded-md bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <Mail className="size-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground font-medium">Email Me Directly</p>
                    <p className="text-sm font-semibold truncate">contact@ye-consultancy.com</p>
                  </div>
                </a>

                <a href="tel:+447490984430" className="flex items-center gap-3 p-3 rounded-lg bg-background border border-border text-foreground hover:border-primary/50 transition-[border-color,box-shadow] shadow-2xs group">
                  <div className="p-2 rounded-md bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <Phone className="size-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground font-medium">Direct Telephone</p>
                    <p className="text-sm font-semibold truncate">+44 7490984430</p>
                  </div>
                </a>
              </div>

              <div className="flex items-start gap-3 pt-2 text-xs text-muted-foreground">
                <MapPin className="size-4 shrink-0 text-primary mt-0.5" />
                <span>Windmill Hill Business Park, Whitehill Way, Swindon SN5 6QR, United Kingdom</span>
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="lg:col-span-6">
            <Card className="border-border shadow-lg bg-card rounded-2xl overflow-hidden">
              <CardHeader className="bg-primary text-primary-foreground p-6 sm:p-8">
                <div className="flex items-center justify-between">
                  <Badge className="bg-accent text-accent-foreground hover:bg-accent/90 border-transparent font-medium px-3 py-1 text-xs">
                    Free Consultation
                  </Badge>
                  <span className="text-xs font-medium text-primary-foreground/80 flex items-center gap-1">
                    <Clock className="size-3.5" /> 24h Response Time
                  </span>
                </div>
                <CardTitle className="text-2xl sm:text-3xl font-serif font-bold text-primary-foreground mt-3">
                  Book Your Free SEO Review
                </CardTitle>
                <CardDescription className="text-primary-foreground/80 text-sm mt-1">
                  Fill in your details below to schedule a direct 1-on-1 strategy call with Yassine.
                </CardDescription>
              </CardHeader>

              <CardContent className="p-6 sm:p-8">
                <FormProvider formId="consultation-cta-form" sectionName="ConsultationCTA" defaultValues={{
                name: "",
                email: "",
                website: "",
                service: "National SEO",
                message: ""
              }} successMessage="I have received your request and will review your website details personally. Expect a response in your inbox within 24 hours." errorMessage="Something went wrong. Please try again.">
                  <InnerConsultationForm />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}